"use client";
import { useMemo } from "react";
import { useMap } from "react-map-gl/mapbox";
import { SearchResultProps } from "@/utils/types";
import { useSearchStore } from "@/store/search";
import { SEARCH_MIN_QUERY } from "@/utils/constants";
import { getLang } from "@/utils/selectors";
import Button from "@/components/common/Button";
import styles from "../Search.module.css";

export interface SearchResultsProps {
	onShowResults: (showResults: boolean) => void;
};

export default function SearchResults({
	onShowResults
} : SearchResultsProps) {
	const { default: map } = useMap();
	const searchQuery = useSearchStore(store => store.searchQuery);
	const setSearchQuery = useSearchStore(store => store.setSearchQuery);
	const searchResults = useSearchStore(store => store.searchResults);
	const setSearchResults = useSearchStore(store => store.setSearchResults);
	const isSearching = useSearchStore(store => store.isSearching);

	const queryTooShort = useMemo(() =>
		searchQuery?.length < SEARCH_MIN_QUERY
	, [searchQuery]);

	const message = useMemo(() => {
		if(queryTooShort) {
			// If query is too short
			// setSearchStatus("chars");
			return getLang("search", "message", "chars", {
				query: searchQuery,
				chars: SEARCH_MIN_QUERY - searchQuery.length
			});
		} else if(isSearching) {
			// If is awaiting results
			// setSearchStatus("wait");
			return getLang("search", "message", "wait", {
				query: searchQuery
			});
		} else if(!searchResults?.length) {
			// If query has no results
			// setSearchStatus("empty");
			return getLang("search", "message", "empty", {
				query: searchQuery
			});
		} else if(searchResults?.length) {
			// If query has results
			// setSearchStatus("show");
			return getLang("search", "message", "show", {
				query: searchQuery
			});
		}
	}, [searchQuery, isSearching]);

	async function handleClick(result: SearchResultProps) {
		// Reset search query
		setSearchQuery("");
		// Clear search results
		setSearchResults(null);
		// Hide search results popver
		onShowResults(false);
		// Set map to bounding box or coordinates from selected result
		if(result.bbox) {
			map?.fitBounds(result.bbox)
		} else if(result.coordinates) {
			const { latitude, longitude } = result.coordinates;
			const coordinates = {
				lat: latitude,
				lng: longitude
			};
			map?.flyTo({
				center: coordinates,
				zoom: 4,
				// speed: 0.1,
				// curve: 1
			});
		}
	}

	return (
		<div
			className={styles.SearchResults}
		>
			{message ?
				<div className={styles.SearchResultsMessage}>
					{message}
				</div>
			: null}
			{searchResults ?
				<ul className={styles.SearchResultsList}>
					{searchResults.map((result, index) =>
						<li
							key={index}
							className={styles.SearchResultsListItem}
						>
							<Button
								color="secondary"
								onClick={() => handleClick(result)}
								className={styles.SearchResultsListItemButton}
							>
								{result.name}
							</Button>
						</li>
					)}
				</ul>
			: null}
		</div>
	)
}